import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Fab,
  InputAdornment,
  Snackbar,
  Alert,
  OutlinedInput,
  Divider,
  Stack,
  TablePagination,
  CircularProgress,
  FormHelperText,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import DeleteConfirmDialog from "../../DeleteConfirmDialog";
import apiEndpoints from "../../../apiconfig";
import ReactDOM from "react-dom";
import DropDownField from "../../../DropDownField";

const ClassSetup = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editClass, setEditClass] = useState(null);
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);

  const [formData, setFormData] = useState({
    branch_guid: "",
    name: "",
    capacity: "",
    required_age: "",
    required_age_max: "",
    is_age_restricted: 1,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const { showSnackbar } = useSnackbar();
  const truncate = (text, length = 25) =>
    text.length > length ? text.substring(0, length) + "..." : text;

  // Fetch classes and branches from API
  useEffect(() => {
    fetchClasses();
    fetchBranches();
  }, []);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiEndpoints.class}?action=getAll`);
      const result = await response.json();

      if (result.status) {
        setClasses(result.data || []);
      } else {
        showSnackbar("Failed to fetch classes", "error");
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      showSnackbar("Error fetching classes", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Lock scroll
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    return () => {
      // Restore scroll when modal closes
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem("token"); // ⬅ ensure token stored after login

      const response = await fetch(`${apiEndpoints.dropdown}?type=branch`, {
        headers: {
          Authorization: `Bearer ${token}`, // ⬅ REQUIRED
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (result.status === "success") {
        setBranches(result.data);
      } else {
        showSnackbar(result.msg || "Failed to load branches", "error");
      }
    } catch (error) {
      console.error("Branch fetch error:", error);
      showSnackbar("Error loading branch list", "error");
    }
  };

  const resetForm = () => {
    setFormData({
      branch_guid: "",
      name: "",
      capacity: "",
      required_age: "",
      required_age_max: "",
      is_age_restricted: 1,
      isActive: 1,
    });
    setEditClass(null);
    setErrors({});
  };

  const handleOpen = (cls = null) => {
    if (cls) {
      setEditClass(cls);
      setFormData({
        branch_guid: cls.branch_guid || "",
        name: cls.name || "",
        capacity: cls.capacity || "",
        required_age: cls.required_age !== null && cls.required_age !== undefined ? cls.required_age : "",
        required_age_max: cls.required_age_max !== null && cls.required_age_max !== undefined ? cls.required_age_max : "",
        is_age_restricted: cls.is_age_restricted !== null && cls.is_age_restricted !== undefined ? (Number(cls.is_age_restricted) === 1 ? 1 : 0) : 1,
        isActive: cls.isActive || 1,
      });
    } else {
      resetForm();
    }
    setOpenForm(true);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.branch_guid) {
      newErrors.branch_guid = "Branch is required";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Class name is required";
    }

    if (!formData.capacity) {
      newErrors.capacity = "Max capacity is required";
    } else if (formData.capacity <= 0) {
      newErrors.capacity = "Enter a valid positive number";
    } else if (!/^\d+$/.test(formData.capacity)) {
      newErrors.capacity = "Capacity must be a whole number";
    }

    if (Number(formData.is_age_restricted) === 1) {
      if (formData.required_age === "" || formData.required_age === null || formData.required_age === undefined) {
        newErrors.required_age = "Min age is required";
      } else if (isNaN(Number(formData.required_age)) || Number(formData.required_age) <= 0) {
        newErrors.required_age = "Enter a valid positive age";
      }

      if (formData.required_age_max === "" || formData.required_age_max === null || formData.required_age_max === undefined) {
        newErrors.required_age_max = "Max age is required";
      } else if (isNaN(Number(formData.required_age_max)) || Number(formData.required_age_max) <= 0) {
        newErrors.required_age_max = "Enter a valid positive age";
      }

      if (!newErrors.required_age && !newErrors.required_age_max) {
        if (Number(formData.required_age) > Number(formData.required_age_max)) {
          newErrors.required_age_max = "Max age must be greater than or equal to min age";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      let url = "";
      let method = "POST";

      if (editClass) {
        // UPDATE Existing Entry ✔
        url = `${apiEndpoints.class}?action=update&id=${editClass.id}`;
      } else {
        // ADD New Entry ✔
        url = `${apiEndpoints.class}?action=add`;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.status) {
        showSnackbar(result.msg || "Success", "success");
        fetchClasses(); // Refresh list
        setOpenForm(false); // Close modal
        resetForm();
      } else {
        showSnackbar(result.msg || "Failed", "error");
      }
    } catch (error) {
      console.error(error);
      showSnackbar("Error occurred", "error");
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(
        `${apiEndpoints.class}?action=delete&id=${selectedDeleteId}`,
      );
      const result = await response.json();

      if (result.status) {
        showSnackbar(result.msg || "Deleted Successfully", "success");
        fetchClasses();
      } else {
        showSnackbar(result.msg || "Delete Failed", "error");
      }
    } catch (error) {
      console.error(error);
    }

    setDeleteDialogOpen(false);
    setSelectedDeleteId(null);
  };

  const filteredClasses = classes.filter(
    (cls) =>
      cls.name.toLowerCase().includes(search.toLowerCase()) ||
      (cls.branch_name &&
        cls.branch_name.toLowerCase().includes(search.toLowerCase())),
  );

  const getBranchName = (branchGuid) => {
    const branch = branches.find((b) => b.guid === branchGuid);
    return branch ? branch.name : "Not Assigned";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "rgba(255, 255, 255, 0.25)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
                radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
              `,
          pointerEvents: "none",
        },
        p: 3,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* LEFT SIDE — Title + Subtitle */}
        <Box>
          <Typography variant="h4" fontWeight={700} mb={1}>
            Class Setup
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure Montessori class groups and assign teachers.
          </Typography>
        </Box>

        {/* RIGHT SIDE — Floating Add Button */}
        <Fab
          color="primary"
          size="medium"
          sx={{
            boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
            flexShrink: 0,
            width: { xs: 48, sm: 56, md: 56 },
            height: { xs: 48, sm: 56, md: 56 },
            "& .MuiSvgIcon-root": {
              fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.6rem" },
            },
            mr: { xs: 1, sm: 0 },
          }}
          onClick={() => {
            setEditClass(null);
            setOpenForm(true);
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* Search Bar */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: "100%", md: "40%", lg: "30%" }, // 🔥 improved responsive
            mb: 2,
            mx: "flex-end", // 🔥 centers inside container at mid screens
          }}
        >
          <TextField
            placeholder="Search classes..."
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: {
                bgcolor: "rgba(255,255,255,0.5)",
                borderRadius: "999px",
              },
            }}
          />
        </Box>
      </Box>

      {/* Class Grid */}
      <Grid container spacing={3}>
        {filteredClasses.length === 0 ? (
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                borderRadius: 3,
                background: "rgba(255, 255, 255, 0.5)",
                backdropFilter: "blur(16px)",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                No classes found. Add your first class!
              </Typography>
            </Paper>
          </Grid>
        ) : (
          filteredClasses
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((cls) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={4}
                key={cls.id}
                sx={{ display: "flex" }}
              >
                <Paper
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    background: "rgba(255,255,255,0.5)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255,255,255,0.5)",
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexDirection: { xs: "column", lg: "row" },
                    gap: 2,
                    width: "100%",
                  }}
                >
                  {/* Left */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                      {truncate(cls.name, 25)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Branch: {getBranchName(cls.branch_guid)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      🧒 Max Capacity: <strong>{cls.capacity}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Age Verification:{" "}
                      <strong>
                        {Number(cls.is_age_restricted) === 1
                          ? `Restricted (${cls.required_age !== null ? (cls.required_age_max !== null ? `${cls.required_age} - ${cls.required_age_max} Yrs` : `${cls.required_age} Yrs`) : "N/A"})`
                          : "Unrestricted"}
                      </strong>
                    </Typography>
                    <Typography variant="body2">
                      Status:{" "}
                      <strong
                        style={{
                          color: cls.isActive ? "green" : "red",
                        }}
                      >
                        {cls.isActive ? "Active" : "Inactive"}
                      </strong>
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={1}
                    >
                      Created: {new Date(cls.createdOn).toLocaleDateString()}
                    </Typography>
                  </Box>

                  {/* Right - Actions */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton onClick={() => handleOpen(cls)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setSelectedDeleteId(cls.id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))
        )}
      </Grid>

      {/* Pagination */}
      {filteredClasses.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "right", mt: 4 }}>
          <TablePagination
            component="div"
            count={filteredClasses.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[6, 12, 24]}
          />
        </Box>
      )}

      {/* Form Drawer */}
      {openForm &&
        ReactDOM.createPortal(
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              bgcolor: "rgba(15,23,42,0.45)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(12px)",
              display: "flex",
              justifyContent: "flex-end",
            }}
            onClick={() => setOpenForm(false)}
          >
            {/* RIGHT PANEL */}
            <Box
              sx={{
                width: { xs: "100%", sm: "80%", md: "55%" },
                height: "100vh",
                p: { xs: 2, md: 3 },
                boxSizing: "border-box",
                display: "flex",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Paper
                elevation={6}
                sx={{
                  position: "relative",
                  width: "100%",
                  maxHeight: "67vh",
                  minHeight: "auto",
                  borderRadius: 4,
                  p: { xs: 3, md: 4 },
                  bgcolor: "rgba(255,255,255,0.96)",
                  backdropFilter: "blur(18px)",
                  overflowY: "auto",
                  boxShadow: "0 18px 45px rgba(15, 23, 42, 0.35)",
                  animation: "slideIn 0.35s ease",

                  // 👇 This moves card down (responsive)
                  mt: { xs: "18vh", sm: "12vh", md: "10vh" },
                }}
              >
                <IconButton
                  onClick={() => setOpenForm(false)}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    bgcolor: "rgba(15,23,42,0.04)",
                    "&:hover": { bgcolor: "rgba(15,23,42,0.08)" },
                  }}
                >
                  <CloseIcon />
                </IconButton>

                {/* HEADER */}
                <Box sx={{ mb: 3, pr: 5 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    🏫 {editClass ? "Edit Class" : "Add New Class"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mt: 0.5 }}
                  >
                    Configure the class details. Fields marked with * are
                    required.
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* SCROLLABLE FORM */}
                <Box sx={{ flex: 1, overflowY: "auto", pr: 1, pt: 1 }}>
                  <Grid container spacing={3}>
                    {/* Branch */}
                    <Grid item xs={12}>
                      <DropDownField
                        label="Branch *"
                        value={formData.branch_guid}
                        options={branches}
                        error={errors.branch_guid}
                        onChange={(guid) =>
                          setFormData((prev) => ({
                            ...prev,
                            branch_guid: guid,
                          }))
                        }
                      />
                    </Grid>

                    {/* Class Name */}
                    <Grid item xs={12}>
                      <TextField
                        label="Class Name *"
                        fullWidth
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>

                    {/* Max Capacity */}
                    <Grid item xs={12}>
                      <TextField
                        label="Max Capacity *"
                        type="number"
                        value={formData.capacity}
                        fullWidth
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            capacity: e.target.value,
                          })
                        }
                        error={Boolean(errors.capacity)}
                        helperText={errors.capacity}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>

                    {/* Age Restriction Switch Toggle */}
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Number(formData.is_age_restricted) === 1}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                is_age_restricted: e.target.checked ? 1 : 0,
                                required_age: e.target.checked ? formData.required_age : "",
                                required_age_max: e.target.checked ? formData.required_age_max : "",
                              })
                            }
                          />
                        }
                        label="Enable Admission Age Restriction"
                      />
                    </Grid>

                    {/* Required Age Field (Conditional) */}
                    {Number(formData.is_age_restricted) === 1 && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Min Age (Years) *"
                            type="number"
                            inputProps={{ step: "0.1", min: "0" }}
                            value={formData.required_age}
                            fullWidth
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                required_age: e.target.value,
                              })
                            }
                            error={Boolean(errors.required_age)}
                            helperText={errors.required_age}
                            InputProps={{ sx: { borderRadius: 3 } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Max Age (Years) *"
                            type="number"
                            inputProps={{ step: "0.1", min: "0" }}
                            value={formData.required_age_max}
                            fullWidth
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                required_age_max: e.target.value,
                              })
                            }
                            error={Boolean(errors.required_age_max)}
                            helperText={errors.required_age_max || "Dynamic DOB range will generate based on cutoff date"}
                            InputProps={{ sx: { borderRadius: 3 } }}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Status (for editing) */}
                    {editClass && (
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={formData.isActive}
                            label="Status"
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                isActive: e.target.value,
                              })
                            }
                            input={
                              <OutlinedInput
                                label="Status"
                                sx={{ borderRadius: 3 }}
                              />
                            }
                          >
                            <MenuItem value={1}>Active</MenuItem>
                            <MenuItem value={0}>Inactive</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>

                  {/* Bottom Actions */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    justifyContent="flex-end"
                    sx={{ mt: 4 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => setOpenForm(false)}
                      sx={{ borderRadius: 999, px: 3 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      sx={{
                        borderRadius: 999,
                        px: 4,
                        fontWeight: 600,
                      }}
                    >
                      {editClass ? "Update Class" : "Save Class"}
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            </Box>
          </Box>,
          document.body,
        )}

      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Delete Class"
        message="Are you sure you want to delete this class?"
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default ClassSetup;
